<script setup lang="ts">
import { computed } from "vue"
import { useRoute } from "vue-router/composables"
import { isNullish } from "@tinyurldev/tu-ui-vue"

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue"
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore.ts"
import { XAuthStore, type UpdateContactParams } from "@Modules/users/Store/XAuthStore"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts"
import OrgUserProfileSettings from "@Modules/users/Views/Settings/OrgUserProfileSettings.vue"
import useLegacyStore from "@Composables/useLegacyStore"

//
// Setup
const route = useRoute()
const teams = XTeamsStore()
const auth = XAuthStore()
const legacyStore = useLegacyStore()

//
// Computed

/** The slug of the current organization and team */
const orgSlug = computed<string>(() => route?.params?.org || "")
const teamSlug = computed<string | null>(() => route.params?.team || null)

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() =>
  teams.getOrganizationBySlug(orgSlug.value),
)

/** The currently chosen workspace. Personal team is 'personal'. Null if no workspace chosen */
const currentWorkspace = computed(() => {
  if (isNullish(teamSlug.value)) {
    return null
  }

  return teams.getWorkspaceBySlugAndTeam(orgSlug.value, teamSlug.value) ?? null
})

/** The currently authenticated user */
const currentUser = computed(() => auth.user)

/** A list of validation errors when submitting the form */
const formErrors = computed(() => auth.errors || {})

/** Loading state for form submissions */
const formLoading = computed(() => auth.loading)

//
// Methods

/** Persist the user's name and email changes */
function onUpdateContact(params: UpdateContactParams): void {
  auth.updateContact(params, legacyStore)
}

/** Persist the user's profile photo change */
function onUpdateIcon(photo: File): void {
  auth.updatePhoto(photo, legacyStore)
}
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization && currentUser"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
  >
    <OrgUserProfileSettings
      :user="currentUser"
      :organization="currentOrganization"
      :errors="formErrors"
      :loading="formLoading"
      @update-contact="onUpdateContact"
      @update-icon="onUpdateIcon"
    />
  </OrgSettingsLayout>
</template>
